import { lazy, Suspense, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import '@/features/music/registerRenderers';
import { AppLayout } from '@/components/layout/AppLayout';
const HomePage = lazy(() => import('./pages/HomePage').then(m => ({ default: m.HomePage })));
import { LoginPage } from '@/features/auth/pages/LoginPage';
import { RegisterPage } from '@/features/auth/pages/RegisterPage';
import { ProtectedRoute } from '@/components/layout/ProtectedRoute';
import { useAuthStore } from '@/features/auth/store/authStore';
import { hasActiveDeviceSession } from '@/features/student-auth/deviceAuth';
import { ErrorBoundary } from '@/components/common/ErrorBoundary';
import { LoadingFallback } from '@/components/common/LoadingFallback';
import { setUnauthorizedHandler } from '@/lib/api-client';
import { useResponsive } from '@/hooks/useResponsive';
import { MobileApp } from '@/components/mobile/layouts/MobileApp';
import { LazyRoute } from './routes/LazyRoute';
import { storyRoutes } from './routes/storyRoutes';
import { quizRoutes } from './routes/quizRoutes';
import { parentRoutes } from './routes/parentRoutes';
import { adminRoutes } from './routes/adminRoutes';
import { flashcardRoutes } from './routes/flashcardRoutes';
import { fluencyRoutes } from './routes/fluencyRoutes';
import { musicRoutes } from './routes/musicRoutes';
import { studentAuthRoutes } from './routes/studentAuthRoutes';

// Lazy load remaining standalone pages
const AnalyticsPage = lazy(() => import('@/features/analytics/pages/AnalyticsPage'));
const AssignmentsPage = lazy(() => import('@/features/student/pages/AssignmentsPage'));
const ProfilePage = lazy(() => import('@/features/profile/pages/ProfilePage'));
const AdminLoginPage = lazy(() => import('@/features/auth/pages/AdminLoginPage'));
const FluencyKidViewPage = lazy(() => import('@/features/fluency/pages/FluencyKidViewPage'));

function AppRoutes() {
  const navigate = useNavigate();
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const logout = useAuthStore((state) => state.logout);

  useEffect(() => {
    setUnauthorizedHandler(() => {
      logout();
      navigate('/login', { replace: true, state: { sessionExpired: true } });
    });
  }, [logout, navigate]);

  // A paired kid on a shared device is also "signed in" for gating purposes.
  const hasSession = isAuthenticated || hasActiveDeviceSession();

  return (
    <Routes>
      {/* Public routes */}
      <Route path="/login" element={hasSession ? <Navigate to="/" replace /> : <LoginPage />} />
      <Route path="/admin/login" element={hasSession ? <Navigate to="/" replace /> : <AdminLoginPage />} />
      <Route path="/register" element={hasSession ? <Navigate to="/" replace /> : <RegisterPage />} />
      <Route
        path="/fluency/kid/:sessionId"
        element={
          <Suspense fallback={<LoadingFallback />}>
            <FluencyKidViewPage />
          </Suspense>
        }
      />

      {/* Kid device sign-in — unauthenticated */}
      {studentAuthRoutes}

      {/* Protected routes */}
      <Route element={<ProtectedRoute />}>
        <Route element={<AppLayout />}>
          <Route path="/" element={<LazyRoute><HomePage /></LazyRoute>} />

          {/* Feature route groups */}
          {storyRoutes}
          {quizRoutes}
          {parentRoutes}
          {adminRoutes}
          {flashcardRoutes}
          {fluencyRoutes}
          {musicRoutes}

          {/* Standalone pages */}
          <Route path="/analytics" element={<LazyRoute fallbackMessage="Loading analytics..."><AnalyticsPage /></LazyRoute>} />
          <Route path="/assignments" element={<LazyRoute><AssignmentsPage /></LazyRoute>} />
          <Route path="/profile" element={<LazyRoute><ProfilePage /></LazyRoute>} />
        </Route>
      </Route>

      {/* Fallback */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function AppContent() {
  const { isMobile, isTablet, isTouchDevice } = useResponsive();
  const location = useLocation();
  const MOBILE_ENABLED = import.meta.env.VITE_ENABLE_MOBILE === 'true';

  const isOnMobileRoute = location.pathname.startsWith('/mobile/');

  // Use mobile layout for phones, touch tablets, or explicit mobile routes.
  // Non-touch laptops/desktops always use the desktop layout regardless of window width.
  if (isMobile || (isTablet && isTouchDevice) || isOnMobileRoute || MOBILE_ENABLED) {
    return <MobileApp />;
  }

  return <AppRoutes />;
}

function App() {
  return (
    <ErrorBoundary>
      <BrowserRouter>
        <AppContent />
      </BrowserRouter>
    </ErrorBoundary>
  );
}

export default App;
